import { useEffect, useRef } from 'react';

/**
 * The hCaptcha challenge Sleeper puts in front of a sign-in.
 *
 * The one part of connecting the league chat that a person has to do: Sleeper
 * asks whoever is signing in to prove they are human, and the honest answer is
 * to show the challenge to the human who is already sitting here rather than to
 * dress the request up as one.
 *
 * The script is loaded when this renders and not before — it is the only
 * third-party code the site pulls in, and no page but the settings page ever
 * asks for it.
 */

const SCRIPT = 'https://js.hcaptcha.com/1/api.js?render=explicit';

interface HCaptcha {
  render: (
    element: HTMLElement,
    options: { sitekey: string; theme?: string; callback: (token: string) => void },
  ) => string;
  reset: (id?: string) => void;
}

declare global {
  interface Window {
    hcaptcha?: HCaptcha;
  }
}

/** Loads hCaptcha's script once, however many times this component mounts. */
let loading: Promise<HCaptcha> | null = null;

function loadHCaptcha(): Promise<HCaptcha> {
  if (window.hcaptcha) return Promise.resolve(window.hcaptcha);
  if (loading) return loading;

  loading = new Promise<HCaptcha>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.hcaptcha) resolve(window.hcaptcha);
      else reject(new Error('hCaptcha loaded but did not register itself.'));
    };
    script.onerror = () => {
      loading = null;
      reject(new Error('hCaptcha could not be loaded.'));
    };
    document.head.append(script);
  });

  return loading;
}

export function Captcha({
  siteKey,
  onSolved,
  onError,
}: {
  siteKey: string;
  onSolved: (token: string) => void;
  /** Called when the widget itself will not run — a wrong or barred site key. */
  onError: (message: string) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  // Held in a ref so re-renders between mounting and solving cannot swap the
  // callback out from under the widget, which is rendered exactly once.
  const solved = useRef(onSolved);
  solved.current = onSolved;
  const failed = useRef(onError);
  failed.current = onError;

  useEffect(() => {
    let cancelled = false;

    loadHCaptcha()
      .then((hcaptcha) => {
        if (cancelled || !box.current || box.current.childElementCount > 0) return;
        hcaptcha.render(box.current, {
          sitekey: siteKey,
          theme: 'dark',
          callback: (token) => solved.current(token),
        });
      })
      .catch((error: unknown) => {
        if (!cancelled) failed.current(error instanceof Error ? error.message : String(error));
      });

    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  return <div ref={box} className="mt-3" />;
}
